import { objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray, defineProperty as _defineProperty } from '../_virtual/_rollupPluginBabelHelpers.js';
import { applyFacet, removeFacet } from './SearchContextFacetManipulations.js';

var SET_CURRENT_QUERY = 'SET_CURRENT_QUERY';
var ADD_RESULTS = 'ADD_RESULTS';
var APPLY_FACET = 'APPLY_FACET';
var REMOVE_FACET = 'REMOVE_FACET';
var UPDATE_FACET_SELECTIONS = 'UPDATE_FACET_SELECTIONS';
var APPLY_SORT = 'APPLY_SORT';
var CLEAR_RESULTS = 'CLEAR_RESULTS';
var RESET_RESULTS_AND_FACETS = 'RESET_RESULTS_AND_FACETS';
var RESET_RESULTS = 'RESET_RESULTS';
var SET_ALL_RESULTS_URL = 'SET_ALL_RESULTS_URL';
var ADD_RENDERER = 'ADD_RENDERER';
var SearchContextReducer = function SearchContextReducer(state, action) {
  switch (action.type) {
    case SET_CURRENT_QUERY:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        currentQuery: action.value
      });
    case RESET_RESULTS_AND_FACETS:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        previousResults: _objectSpread2({}, state.results),
        availableFacets: {},
        totalCount: {},
        hasToAppendResults: {},
        reportedResultPluginCount: 0
      });
    case RESET_RESULTS:
      var preservedFacets = Object.keys(state.appliedFacets).reduce(function (taxonomiesWithFacets, taxonomy) {
        taxonomiesWithFacets[taxonomy] = Object.keys(state.appliedFacets[taxonomy]).reduce(function (facetsToKeep, facetName) {
          var _state$availableFacet;
          facetsToKeep[facetName] = (_state$availableFacet = state.availableFacets[taxonomy]) === null || _state$availableFacet === void 0 ? void 0 : _state$availableFacet[facetName];
          return facetsToKeep;
        }, {});
        return taxonomiesWithFacets;
      }, {});
      return _objectSpread2(_objectSpread2({}, state), {}, {
        previousResults: _objectSpread2({}, state.results),
        results: {},
        availableFacets: preservedFacets,
        totalCount: {},
        reportedResultPluginCount: 0
      });
    case CLEAR_RESULTS:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        results: {},
        previousResults: {},
        hasToAppendResults: {}
      });
    case ADD_RESULTS:
      var resultAction = action;
      var newResults = _objectSpread2({}, state.results);
      if (!state.hasToAppendResults[resultAction.taxonomyId]) {
        newResults[resultAction.taxonomyId] = _toConsumableArray(resultAction.results);
      } else {
        var _state$results$result;
        newResults[resultAction.taxonomyId] = [].concat(_toConsumableArray((_state$results$result = state === null || state === void 0 ? void 0 : state.results[resultAction.taxonomyId]) !== null && _state$results$result !== void 0 ? _state$results$result : []), _toConsumableArray(resultAction.results));
      }
      var newTotalCount = _objectSpread2({}, state.totalCount);
      newTotalCount[resultAction.taxonomyId] = resultAction.totalCount;

      //Todo: maybe shared facet-merge function here and with the provider availableFacets function?
      //Goal - add facets and facet value result counts to the taxonomy's list of current facets and facet values, while preserving any data that has already been returned by other result plugins. The end result should be a combined record of all possible facets and values from the result plugins for a taxonomy, adding result counts to the facet values rather than duplicating entries.
      var newFacets = resultAction.availableFacets ? _objectSpread2({}, state.availableFacets) : state.availableFacets;
      if (resultAction.availableFacets) {
        //It is possible this is the first/only plugin to return facets, so prepare the top level record for the taxonomy
        if (!newFacets[resultAction.taxonomyId]) {
          newFacets[resultAction.taxonomyId] = {};
        }
        Object.keys(resultAction.availableFacets).forEach(function (facet) {
          //It is possible that this facet key (facetable attributes) is brand new, either because this is the first result plugin for the taxonomy or other result plugins did not have this facet, so we must prepare the object for this facet.
          if (!newFacets[resultAction.taxonomyId][facet]) {
            newFacets[resultAction.taxonomyId][facet] = {};
          }
          Object.keys(resultAction.availableFacets[facet]).forEach(function (facetValue) {
            //It is possible this is a new value for the facet
            if (!newFacets[resultAction.taxonomyId][facet][facetValue]) {
              newFacets[resultAction.taxonomyId][facet][facetValue] = 0;
            }
            //In the case that this facet key and value already were returned from the another result plugin, we want to increase the number of results, not override.
            newFacets[resultAction.taxonomyId][facet][facetValue] += resultAction.availableFacets[facet][facetValue];
          });
        });
      }
      var newSectionTrackingDetails = resultAction.sectionTrackingDetails ? _objectSpread2({}, state.sectionTrackingDetails) : state.sectionTrackingDetails;
      if (resultAction.sectionTrackingDetails) {
        if (!newSectionTrackingDetails[resultAction.taxonomyId]) {
          newSectionTrackingDetails[resultAction.taxonomyId] = {};
        }
        //Make this more generic if/when additional fields are added. Until that time no need to increase complexity and reduce readability
        if (newSectionTrackingDetails[resultAction.taxonomyId].rules && resultAction.sectionTrackingDetails) {
          newSectionTrackingDetails[resultAction.taxonomyId].rules = newSectionTrackingDetails[resultAction.taxonomyId].rules.concat(resultAction.sectionTrackingDetails.rules || []);
        } else if (resultAction.sectionTrackingDetails) {
          newSectionTrackingDetails[resultAction.taxonomyId].rules = resultAction.sectionTrackingDetails.rules;
        }
      }
      return _objectSpread2(_objectSpread2({}, state), {}, {
        partialResults: newResults,
        results: newResults,
        totalCount: newTotalCount,
        availableFacets: newFacets,
        reportedResultPluginCount: resultAction.shouldReportComplete ? state.reportedResultPluginCount + 1 : state.reportedResultPluginCount,
        sectionTrackingDetails: newSectionTrackingDetails,
        hasToAppendResults: _objectSpread2(_objectSpread2({}, state.hasToAppendResults), {}, _defineProperty({}, resultAction.taxonomyId, true))
      });
    case APPLY_FACET:
      {
        var facetAction = action;
        var appliedFacets = applyFacet(state.appliedFacets, facetAction, facetAction.taxonomyId);
        return _objectSpread2(_objectSpread2({}, state), {}, {
          appliedFacets: appliedFacets
        });
      }
    case REMOVE_FACET:
      {
        var _facetAction = action;
        var _appliedFacets = removeFacet(state.appliedFacets, _facetAction, _facetAction.taxonomyId);
        return _objectSpread2(_objectSpread2({}, state), {}, {
          appliedFacets: _appliedFacets
        });
      }
    case UPDATE_FACET_SELECTIONS:
      {
        var _facetAction2 = action;
        var _appliedFacets2 = _objectSpread2({}, state.appliedFacets);
        if (Array.isArray(_facetAction2.facetsToApply)) {
          _appliedFacets2 = _facetAction2.facetsToApply.reduce(function (newAppliedfacets, currentFacetToApply) {
            return applyFacet(newAppliedfacets, currentFacetToApply, _facetAction2.taxonomyId);
          }, _appliedFacets2);
        } else {
          _appliedFacets2 = applyFacet(state.appliedFacets, _facetAction2.facetsToApply, _facetAction2.taxonomyId);
        }
        if (Array.isArray(_facetAction2.facetsToRemove)) {
          _appliedFacets2 = _facetAction2.facetsToRemove.reduce(function (newAppliedfacets, currentFacetToRemove) {
            return removeFacet(newAppliedfacets, currentFacetToRemove, _facetAction2.taxonomyId);
          }, _appliedFacets2);
        } else {
          _appliedFacets2 = removeFacet(_appliedFacets2, _facetAction2.facetsToRemove, _facetAction2.taxonomyId);
        }
        return _objectSpread2(_objectSpread2({}, state), {}, {
          appliedFacets: _appliedFacets2
        });
      }
    case APPLY_SORT:
      var sortStrategies = _objectSpread2({}, state.sortStrategies);
      var taxonomies = [];
      if (typeof action.taxonomyId === 'string') {
        taxonomies.push(action.taxonomyId);
      } else {
        taxonomies = action.taxonomyId;
      }
      taxonomies.forEach(function (taxonomy) {
        sortStrategies[taxonomy] = action.sort;
      });
      return _objectSpread2(_objectSpread2({}, state), {}, {
        sortStrategies: sortStrategies
      });
    case SET_ALL_RESULTS_URL:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        allResultsUrl: action.value
      });
    case ADD_RENDERER:
      var newRenders = _objectSpread2({}, state.renderers);
      newRenders[action.taxonomyId] = action.renderer;
      return _objectSpread2(_objectSpread2({}, state), {}, {
        renderers: newRenders
      });
    default:
      console.warn("unhandled reducer action: ".concat(action.type));
      return _objectSpread2({}, state);
  }
};

export { ADD_RENDERER, ADD_RESULTS, APPLY_FACET, APPLY_SORT, CLEAR_RESULTS, REMOVE_FACET, RESET_RESULTS, RESET_RESULTS_AND_FACETS, SET_ALL_RESULTS_URL, SET_CURRENT_QUERY, SearchContextReducer, UPDATE_FACET_SELECTIONS };
//# sourceMappingURL=SearchContextReducer.js.map
